import React from 'react';
import {
  Autocomplete,
  Box,
  type SxProps,
  TextField,
  type Theme,
  Typography,
  useTheme,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { alpha } from '@mui/material/styles';
import { borderRadius, getFieldBorderColor } from '../../../theme/theme';
import { getHelpPreviewText } from '../common/helpModel';

/**
 * Standardized option interface for all dropdowns across the app
 */
export interface DropdownOption {
  value: string;
  label: string;
  occurrenceKey?: string;
  tooltip?: React.ReactNode;
  description?: React.ReactNode;
  group?: string;
  // Shared parameter file DATA token (TEXT, INTEGER, LENGTH, ...); absent when unknown.
  dataType?: string;
}

interface InternalDropdownOption extends DropdownOption {
  optionKey: string;
}

/**
 * Props for the centralized SearchableDropdown component
 */
export interface SearchableDropdownProps {
  /** The current value */
  value: string;
  /** Callback fired when the value changes */
  onChange: (value: string) => void;
  /** Array of options to display */
  options: DropdownOption[];
  /** Placeholder text */
  placeholder?: string;
  /** Error state/message */
  error?: boolean | string;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Size of the component */
  size?: 'small' | 'medium';
  /** Full width */
  fullWidth?: boolean;
  /** Allow free text input */
  freeSolo?: boolean;
  /** When false in freeSolo mode, typed text is committed on blur/Enter instead of every keypress */
  commitOnInput?: boolean;
  /** Focus the input when the dropdown mounts */
  autoFocus?: boolean;
  /** Called after a deferred free-text value is committed with Enter */
  onCommit?: () => void;
  /** Custom sx props */
  sx?: SxProps<Theme>;
  /** aria-label for accessibility */
  ariaLabel?: string;
}

/**
 * Centralized searchable dropdown component used across the entire application.
 * All dropdowns should use this component for consistency.
 *
 * @example
 * ```tsx
 * <SearchableDropdown
 *   value={selectedValue}
 *   onChange={setSelectedValue}
 *   options={[
 *     { value: 'opt1', label: 'Option 1' },
 *     { value: 'opt2', label: 'Option 2', tooltip: 'With tooltip' },
 *   ]}
 *   placeholder="Search options..."
 * />
 * ```
 */
const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  value,
  onChange,
  options,
  placeholder = 'Search…',
  error = false,
  disabled = false,
  size = 'small',
  fullWidth = true,
  freeSolo = false,
  commitOnInput = true,
  autoFocus = false,
  onCommit,
  sx = {},
  ariaLabel,
}) => {
  const theme = useTheme();
  const fieldBorderColor = getFieldBorderColor(theme);
  const controlLabel = ariaLabel || placeholder;
  const indicatorColor = alpha(theme.palette.text.secondary, theme.palette.mode === 'dark' ? 0.78 : 0.72);
  const indicatorHoverBg = alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.12 : 0.08);
  const indicatorFocusBg = alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.18 : 0.12);
  const indicatorButtonSx = {
    width: 22,
    height: 22,
    minWidth: 22,
    minHeight: 22,
    p: 0,
    m: 0,
    border: '1px solid transparent',
    borderRadius: '50%',
    color: indicatorColor,
    backgroundColor: 'transparent',
    '&:hover': {
      color: theme.palette.text.primary,
      backgroundColor: indicatorHoverBg,
      borderColor: 'transparent',
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 1,
      color: theme.palette.text.primary,
      backgroundColor: indicatorFocusBg,
      borderColor: 'transparent',
    },
    '& .MuiSvgIcon-root': {
      fontSize: 16,
    },
  };

  // Local state for input value in freeSolo mode
  const [inputValue, setInputValue] = React.useState(value || '');
  const [isOpen, setIsOpen] = React.useState(false);
  const committedValueRef = React.useRef(value || '');

  // Sync inputValue with external value changes
  React.useEffect(() => {
    committedValueRef.current = value || '';
    setInputValue(value || '');
  }, [value]);

  const safeOptions = React.useMemo<InternalDropdownOption[]>(() => {
    const sourceOptions = Array.isArray(options) ? options : [];
    const seen = new Set<string>();

    return sourceOptions.reduce<InternalDropdownOption[]>((accumulator, option, index) => {
      if (!option || typeof option.value !== 'string') {
        return accumulator;
      }

      const normalizedValue = option.value.trim().toLowerCase();
      const normalizedLabel = (option.label || '').trim().toLowerCase();
      const normalizedOccurrenceKey = option.occurrenceKey?.trim().toLowerCase();
      const dedupeKey = normalizedOccurrenceKey
        ? `occurrence:${normalizedOccurrenceKey}`
        : normalizedValue || normalizedLabel || `option-${index}`;

      if (seen.has(dedupeKey)) {
        return accumulator;
      }

      seen.add(dedupeKey);
      accumulator.push({
        ...option,
        optionKey: `${dedupeKey}-${index}`,
      });
      return accumulator;
    }, []);
  }, [options]);

  // Find the selected option (for non-freeSolo mode or when value matches an option)
  const selectedOption = safeOptions.find((opt) => opt.value === value) || null;

  // Determine if there's an error
  const hasError = typeof error === 'string' ? !!error : error;
  const errorMessage = typeof error === 'string' ? error : undefined;
  const outlinedBorderColor = hasError ? theme.palette.error.main : fieldBorderColor;
  const squaredBottomCorners = isOpen ? { borderBottomLeftRadius: 0, borderBottomRightRadius: 0 } : {};

  const commitValue = React.useCallback((nextValue: string) => {
    if (committedValueRef.current === nextValue) {
      return;
    }

    committedValueRef.current = nextValue;
    onChange(nextValue);
  }, [onChange]);

  // Handle change when an option is selected or value is cleared
  const handleChange = (
    _event: React.SyntheticEvent,
    newValue: InternalDropdownOption | string | null
  ) => {
    if (newValue === null) {
      commitValue('');
      setInputValue('');
    } else if (typeof newValue === 'string') {
      commitValue(newValue);
      setInputValue(newValue);
    } else {
      commitValue(newValue.value);
      setInputValue(newValue.label);
    }
  };

  // Handle input value change (for freeSolo typing)
  const handleInputChange = (
    _event: React.SyntheticEvent,
    newInputValue: string,
    reason: string
  ) => {
    setInputValue(newInputValue);

    // In freeSolo mode, update the value on every input change
    if (freeSolo && commitOnInput && reason === 'input') {
      commitValue(newInputValue);
    }
  };

  const handleDeferredCommit = () => {
    if (freeSolo && !commitOnInput) {
      commitValue(inputValue);
    }
  };

  // Filter options based on input
  const filterOptions = (
    options: InternalDropdownOption[],
    { inputValue: filterInput }: { inputValue: string }
  ) => {
    const filterValue = filterInput.toLowerCase();
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(filterValue) ||
        option.value.toLowerCase().includes(filterValue)
    );
  };

  return (
    <Autocomplete
      value={freeSolo ? inputValue : selectedOption}
      inputValue={inputValue}
      onChange={handleChange}
      onInputChange={handleInputChange}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      options={safeOptions}
      getOptionLabel={(option) => {
        if (typeof option === 'string') return option;
        return option?.label || '';
      }}
      isOptionEqualToValue={(option, val) => {
        if (typeof val === 'string') return option.optionKey === selectedOption?.optionKey;
        return option.optionKey === val?.optionKey;
      }}
      filterOptions={filterOptions}
      disabled={disabled}
      size={size}
      fullWidth={fullWidth}
      freeSolo={freeSolo}
      clearOnBlur={!freeSolo}
      selectOnFocus
      handleHomeEndKeys
      noOptionsText="No matching parameters"
      clearIcon={<CloseRoundedIcon />}
      renderInput={(params) => (
        <TextField
          {...params}
          autoFocus={autoFocus}
          placeholder={placeholder}
          error={hasError}
          helperText={errorMessage}
          inputProps={{
            ...params.inputProps,
            'aria-label': controlLabel,
            onBlur: (event) => {
              params.inputProps.onBlur?.(event as React.FocusEvent<HTMLInputElement>);
              handleDeferredCommit();
            },
            onKeyDown: (event) => {
              const isComposing = event.nativeEvent.isComposing || event.which === 229;
              if (!isComposing && event.key === 'Enter' && freeSolo && !commitOnInput) {
                event.preventDefault();
                Object.assign(event, { defaultMuiPrevented: true });
                handleDeferredCommit();
                onCommit?.();
                return;
              }

              params.inputProps.onKeyDown?.(event as React.KeyboardEvent<HTMLInputElement>);
              if (!event.defaultPrevented && event.key === 'Enter' && !isOpen) {
                handleDeferredCommit();
              }
            },
          }}
          sx={[
            {
              '& .MuiOutlinedInput-root': {
                backgroundColor: alpha(
                  theme.palette.background.paper,
                  theme.palette.mode === 'light' ? 0.98 : 0.6
                ),
                transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
                // Square off the bottom corners while the popup is open so the
                // field and paper read as one attached combobox instead of two
                // rounded boxes colliding.
                ...squaredBottomCorners,
                '& fieldset': {
                  borderColor: outlinedBorderColor,
                  ...squaredBottomCorners,
                },
                '&:hover': {
                  backgroundColor: alpha(
                    theme.palette.background.paper,
                    theme.palette.mode === 'light' ? 1 : 0.8
                  ),
                },
                '&:hover fieldset': {
                  borderColor: outlinedBorderColor,
                },
                '&.Mui-focused': {
                  backgroundColor: alpha(
                    theme.palette.background.paper,
                    theme.palette.mode === 'light' ? 1 : 0.9
                  ),
                  boxShadow: `inset 0 0 0 2px ${alpha(
                    theme.palette.divider,
                    theme.palette.mode === 'light' ? 0.22 : 0.18
                  )}`,
                },
                '&.Mui-focused fieldset': {
                  borderColor: outlinedBorderColor,
                  borderWidth: 1,
                },
              },
              '& .MuiAutocomplete-endAdornment': {
                right: 6,
                display: 'flex',
                alignItems: 'center',
                gap: 0.25,
              },
              '& .MuiAutocomplete-clearIndicator, & .MuiAutocomplete-popupIndicator': indicatorButtonSx,
              '& .MuiAutocomplete-clearIndicator': {
                opacity: 0.86,
              },
              '& .MuiSvgIcon-root': {
                color: theme.palette.text.secondary,
              },
              '& .MuiAutocomplete-clearIndicator .MuiSvgIcon-root, & .MuiAutocomplete-popupIndicator .MuiSvgIcon-root': {
                color: 'inherit',
              },
            },
            ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
          ]}
        />
      )}
      renderOption={(props, option) => {
        const isSelected = option.optionKey === selectedOption?.optionKey;
        const description = getHelpPreviewText(option.description ?? option.tooltip);
        return (
          <Box
            component="li"
            {...props}
            key={option.optionKey}
            sx={{
              fontSize: '0.875rem',
              display: 'flex',
              alignItems: 'center',
              backgroundColor: isSelected
                ? alpha(theme.palette.primary.main, 0.16)
                : 'transparent',
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
              },
              '&.Mui-focused': {
                backgroundColor: alpha(theme.palette.primary.main, 0.12),
              },
              ...(isSelected
                ? {
                    '&:hover, &.Mui-focused': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.24),
                    },
                  }
                : {}),
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0, pr: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: isSelected ? 600 : 500,
                  color: theme.palette.text.primary,
                  lineHeight: 1.35,
                }}
              >
                {option.label}
              </Typography>
              {description && (
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    color: theme.palette.text.secondary,
                    lineHeight: 1.45,
                    whiteSpace: 'normal',
                    mt: 0.2,
                  }}
                >
                  {description}
                </Typography>
              )}
            </Box>
            {isSelected && (
              <CheckIcon sx={{ ml: 'auto', color: theme.palette.primary.main, fontSize: 16 }} />
            )}
          </Box>
        );
      }}
      slotProps={{
        paper: {
          sx: {
            maxHeight: theme.spacing(30),
            // Attached-combobox look: overlap the field's bottom border so the
            // junction shows a single continuous outline, not a doubled seam.
            mt: '-1px',
            border: `1px solid ${fieldBorderColor}`,
            borderTop: 'none',
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            borderBottomLeftRadius: `${borderRadius.md}px`,
            borderBottomRightRadius: `${borderRadius.md}px`,
            backgroundColor:
              theme.palette.mode === 'dark'
                ? alpha(theme.palette.background.paper, 0.98)
                : theme.palette.background.paper,
            color: theme.palette.text.primary,
            boxShadow: theme.shadows[8],
          },
        },
        // Keep the popup below the field at all times; the squared top corners
        // assume a downward attachment. preventOverflow still nudges it into view.
        popper: {
          modifiers: [{ name: 'flip', enabled: false }],
        },
        clearIndicator: {
          'aria-label': `Clear ${controlLabel}`,
          sx: indicatorButtonSx,
        },
        popupIndicator: {
          'aria-label': `Open ${controlLabel} options`,
          sx: indicatorButtonSx,
        },
        listbox: {
          sx: {
            maxHeight: theme.spacing(25),
          },
        },
      }}
    />
  );
};

export default SearchableDropdown;

